import { createContext, useContext } from 'react';

import { type NoteOutcome } from '@apps/web/lib/agent_note_outcome_copy';
import { type AgentNoteRead } from '@libraries/typescript/api_client';

export type SavedNoteOutcomes = {
    // The notes themselves, so the Learned tab reads what the provider already
    // fetched instead of running the same poll a second time.
    readonly notes: ReadonlyArray<AgentNoteRead>;
    readonly outcomesByNoteId: ReadonlyMap<string, ReadonlyArray<NoteOutcome>>;
};

const EMPTY: SavedNoteOutcomes = {
    notes: [],
    outcomesByNoteId: new Map(),
};

/**
 * Resolved destinations for the notes in this transcript, keyed by note id.
 *
 * A context rather than a prop because the chip is rendered from deep inside
 * the timeline switch in two separate surfaces; threading a map through both
 * would touch far more code than the feature is worth. A note absent from the
 * map has not settled yet, which the chip renders as its plain self.
 */
export const SavedNoteOutcomesContext = createContext<SavedNoteOutcomes>(EMPTY);

export function useSavedNoteOutcomes(
    noteId: string,
): ReadonlyArray<NoteOutcome> {
    return (
        useContext(SavedNoteOutcomesContext).outcomesByNoteId.get(noteId) ?? []
    );
}

/** Every note in this transcript, as already fetched by the provider. */
export function useSavedNotes(): ReadonlyArray<AgentNoteRead> {
    return useContext(SavedNoteOutcomesContext).notes;
}
